import { useState } from "react";

type FormState = "idle" | "submitting" | "success" | "error";

export default function ContactSection() {
  const [formState, setFormState] = useState<FormState>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > 500) return;

    setFormState("submitting");

    const formData = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name && el.value) {
        formData.append(el.name, el.value);
      }
    }

    try {
      const res = await fetch("https://readdy.ai/api/form/d71j338hb0rqicn68id0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      if (res.ok) {
        setFormState("success");
        form.reset();
        setCharCount(0);
      } else {
        setFormState("error");
      }
    } catch {
      setFormState("error");
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#F8F6F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Top header — centered, compact */}
        <div className="text-center mb-14">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#C9A227] mb-3 block">
            Get in Touch
          </span>
          <h2 className="text-3xl lg:text-4xl font-extrabold text-[#222831] leading-tight">
            Let&apos;s Talk About Your Sourcing Needs
          </h2>
          <p className="mt-3 text-sm text-[#3A506B] max-w-lg mx-auto">
            Drop us a message and someone from our team will follow up directly — no automated replies, no runaround.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">

          {/* Left — info + context */}
          <div className="flex flex-col gap-8">

            {/* Contact card */}
            <div className="rounded-2xl bg-[#222831] text-white p-8 flex flex-col gap-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-[#C9A227] mb-2">Direct Contact</p>
                <a
                  href="mailto:info@ltfsourcing.com"
                  className="text-lg font-bold hover:text-[#C9A227] transition-colors"
                >
                  info@ltfsourcing.com
                </a>
              </div>
              <div className="h-px bg-white/10"></div>
              <div className="flex flex-col gap-4">
                {[
                  {
                    icon: "ri-time-line",
                    title: "Response time",
                    desc: "We aim to reply within 24–48 hours on business days.",
                  },
                  {
                    icon: "ri-user-line",
                    title: "Who replies",
                    desc: "A real person from our sourcing team — not a bot or a template.",
                  },
                  {
                    icon: "ri-lock-line",
                    title: "Your data",
                    desc: "We never share your information with third parties.",
                  },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C9A227]/15 flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#C9A227] text-sm`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{item.title}</p>
                      <p className="text-xs text-white/55 mt-0.5 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* What to include nudge */}
            <div className="rounded-2xl border border-[#C9A227]/30 bg-[#fdf8ee] p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#C9A227] mb-3">Helpful to include</p>
              <ul className="flex flex-col gap-2">
                {[
                  "What product(s) you need sourced or sold",
                  "Approximate quantities or order size",
                  "Your target timeline or urgency",
                  "Any quality or certification requirements",
                ].map((tip) => (
                  <li key={tip} className="flex items-start gap-2 text-sm text-[#3A506B]">
                    <i className="ri-checkbox-circle-line text-[#C9A227] text-base mt-0.5 flex-shrink-0"></i>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right — form */}
          <div className="rounded-2xl bg-white border border-[#E8E0D0] p-8">
            {formState === "success" ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-50 border border-green-200">
                  <i className="ri-check-line text-green-500 text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-[#222831]">Message received!</h3>
                <p className="text-sm text-[#3A506B] max-w-xs">
                  We&apos;ll get back to you within 24–48 hours. Keep an eye on your inbox.
                </p>
                <button
                  onClick={() => setFormState("idle")}
                  className="mt-2 text-sm font-semibold text-[#C9A227] hover:underline cursor-pointer whitespace-nowrap"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#3A506B] uppercase tracking-wide">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      required
                      placeholder="Jane Smith"
                      className="w-full px-4 py-2.5 rounded-lg border border-[#E8E0D0] text-sm text-[#222831] bg-[#FAFAF8] focus:outline-none focus:border-[#C9A227] transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#3A506B] uppercase tracking-wide">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="jane@company.com"
                      className="w-full px-4 py-2.5 rounded-lg border border-[#E8E0D0] text-sm text-[#222831] bg-[#FAFAF8] focus:outline-none focus:border-[#C9A227] transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#3A506B] uppercase tracking-wide">
                      Company
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      placeholder="Acme Corp"
                      className="w-full px-4 py-2.5 rounded-lg border border-[#E8E0D0] text-sm text-[#222831] bg-[#FAFAF8] focus:outline-none focus:border-[#C9A227] transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#3A506B] uppercase tracking-wide">
                      Service Interest
                    </label>
                    <select
                      name="serviceInterest"
                      className="w-full px-4 py-2.5 rounded-lg border border-[#E8E0D0] text-sm text-[#222831] bg-[#FAFAF8] focus:outline-none focus:border-[#C9A227] transition-colors"
                    >
                      <option value="">Select one</option>
                      <option>Product Sourcing</option>
                      <option>Overstock / Bulk Buying</option>
                      <option>Quality Inspection</option>
                      <option>Logistics Coordination</option>
                      <option>Supplier Verification</option>
                      <option>Procurement Strategy</option>
                      <option>List Overstock Inventory</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#3A506B] uppercase tracking-wide flex justify-between">
                    <span>Message *</span>
                    <span className={charCount > 450 ? "text-red-400" : "text-[#3A506B]/50"}>{charCount}/500</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    placeholder="Tell us what you need — product type, quantities, timeline, or any questions..."
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] text-sm text-[#222831] bg-[#FAFAF8] focus:outline-none focus:border-[#C9A227] transition-colors resize-none"
                  />
                </div>

                {formState === "error" && (
                  <p className="text-red-500 text-xs">
                    Something went wrong. Please try again or email us directly at info@ltfsourcing.com.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={formState === "submitting"}
                  className="w-full py-3 rounded-lg bg-[#222831] text-white font-bold text-sm hover:bg-[#C9A227] transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {formState === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-arrow-right-line text-base"></i>
                    </>
                  )}
                </button>

                <p className="text-xs text-[#3A506B]/50 text-center">
                  By submitting you agree to our{" "}
                  <a href="#" rel="nofollow" className="underline hover:text-[#C9A227]">
                    Privacy Policy
                  </a>
                  .
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
